/**
 * V70 엔글 마블 — 서버 API Interface + Phase 2 Mock 구현 (V2)
 *
 * V2 핵심 변경:
 *   1. 단일 저장 레코드 (MarbleStorageRecord) — state + processedRequests 통합
 *      → localStorage 단일 setItem 으로 원자적 커밋
 *   2. START 완주 보상 +300 pt/바퀴 (lap 감지)
 *   3. 무인도 (safe[6]) — 1·2차 ≥4 성공, 3차 보장
 *   4. 보너스 금고 (safe[12]) — A/B 선택
 *   5. 아이템 상점 (safe[18]) — 방패 150 / 가속 100
 *   6. 워프 자유 선택 (23칸)
 *   7. 가속 아이템 (useSpeedBoost in roll)
 *   8. V1 → V2 안전 마이그레이션 (기존 데이터 보존)
 *
 * Phase 3 BE 교체 포인트는 동일. mock 저장 범위: localStorage ("marble.mock.*")
 */
import {
  E1_CONFIG, E2_CONFIG, E3_CONFIG, E4_CONFIG, E5_CONFIG,
  E6_CONFIG, E7_CONFIG, E8_CONFIG, E10_CONFIG,
  MARBLE_GLOBAL_CONFIG, V2_SAFE_ROLES, LAP_REWARD,
  TRAP_CONFIG, VAULT_CONFIG, SHOP_CONFIG, SPEED_CONFIG,
  type EventKind,
} from '../config/eventConfig';
import { generateBoard } from '../engine/boardLayout';
import { createPrng, hashSeed, pickWeighted } from '../engine/prng';
// V70 Phase 3-A: 매 플레이 액션 전 BE 재검증 + 어드민 쓰기 Firestore SSoT (사용자 지시 §1·§3)
// Phase 3-A (후속): 사용자 조회 getter 도 Firestore 기준으로 갱신 → 어드민 설정 전역 적용
import {
  assertTournamentGameAccess,
  callUpdateTournamentOpenPeriod,
  callUpdateTournamentDicePolicy,
  callGetPublicTournamentGameSettings,
  type AccessCheckResp,
} from '../../shared/tournamentGameApi';
import type {
  InProgressEventState, LeaderboardRow, MarbleBoard, MarbleSettings,
  MyState, RollDiceRequest, RollDiceResponse,
  SubmitEventChoiceRequest, SubmitEventChoiceResponse,
  DicePolicy, DicePolicyChange, DailyDiceStatus,
  PurchaseItemRequest, PurchaseItemResponse,
} from '../engine/types';
import { DEFAULT_DICE_POLICY } from '../engine/types';
// V70 Phase 3-C — Feature flag 분기용 (파일 상단 import 유지, 하단에서 사용)
import { isPhase3cServerEnabled } from '../../shared/phase3cFlag';
import { serverMarbleApi } from '../server/serverApi';

export interface MarbleServerApi {
  getSettings(): Promise<MarbleSettings>;
  getBoard(): Promise<MarbleBoard>;
  getMyState(uid: string, displayName: string, orgUnitId?: string): Promise<MyState>;
  rollDice(uid: string, req: RollDiceRequest & { useSpeedBoost?: boolean }): Promise<RollDiceResponse>;
  submitEventChoice(uid: string, req: SubmitEventChoiceRequest): Promise<SubmitEventChoiceResponse>;
  purchaseItem(uid: string, req: PurchaseItemRequest): Promise<PurchaseItemResponse>;
  getLeaderboard(selfUid: string): Promise<{ rows: LeaderboardRow[]; self: LeaderboardRow | null; cachedAt: number }>;
  qaResetMyState(uid: string, clientRequestId?: string): Promise<{ ok: boolean }>;
  qaRefillDice(uid: string, dice: number, clientRequestId?: string): Promise<{ ok: boolean }>;
  qaResetAll(): Promise<{ ok: boolean }>;
  updateOpenPeriod(req: { openStartAt: number | null; openEndAt: number | null }): Promise<{ success: boolean; openStartAt: number | null; openEndAt: number | null; isMock: boolean }>;
  getDicePolicy(): Promise<DicePolicy>;
  getDailyDiceStatus(uid: string): Promise<DailyDiceStatus>;
  updateDicePolicy(req: { policy: DicePolicy; clientRequestId: string }): Promise<{ success: boolean; previous: DicePolicy; next: DicePolicy; changedAt: number; isMock: boolean }>;
  getDicePolicyHistory(opts?: { limit?: number }): Promise<DicePolicyChange[]>;
}

// ─────────── 저장 레코드 (V2) ───────────

export interface MarbleStorageRecord {
  schemaVersion: 2;
  state: MyState;
  processedRequests: Record<string, { response: unknown; ts: number }>;
}

const RECORD_KEY_PREFIX = 'marble.mock.record.';
const KEY_SETTINGS = 'marble.mock.settings';
const KEY_BOARD = 'marble.mock.board';
const KEY_LEADERBOARD_KEYS = 'marble.mock.lb.uids';
const KEY_DICE_POLICY = 'marble.mock.dicePolicy';
const KEY_DICE_POLICY_HISTORY = 'marble.mock.dicePolicyHistory';

// V1 레거시 키 (마이그 전용)
const V1_STATE_KEY_PREFIX = 'marble.mock.myState.';
const V1_RESP_KEY_PREFIX = 'marble.mock.resp.';

const PROCESSED_REQUESTS_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_PROCESSED_REQUESTS = 500;

// ─────────── 손상 레코드 오류 ───────────

/**
 * V2 저장 레코드가 존재하지만 파싱/검증 실패 시 발생.
 * 호출 측에서 상태 변경 요청 전부 차단해야 함.
 * 자동 복원·초기화 금지.
 */
export class V2CorruptError extends Error {
  readonly isV2Corrupt = true as const;
  constructor(public readonly uid: string, message: string) {
    super(message);
    this.name = 'V2CorruptError';
  }
}

// ─────────── localStorage 유틸 ───────────

function safeGet(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
}
function safeRemove(key: string): void {
  try { window.localStorage.removeItem(key); } catch { /* ignore */ }
}
function readJson<T>(key: string, fallback: T): T {
  const raw = safeGet(key);
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}
function writeJson(key: string, value: unknown): void {
  safeSet(key, JSON.stringify(value));
}

function recordKey(uid: string): string { return RECORD_KEY_PREFIX + uid; }

// ─── 주사위 정책 ───
function readDicePolicy(): DicePolicy {
  const raw = safeGet(KEY_DICE_POLICY);
  if (!raw) return { ...DEFAULT_DICE_POLICY };
  try {
    const p = JSON.parse(raw) as DicePolicy;
    if (p.mode !== 'limited' && p.mode !== 'unlimited') return { ...DEFAULT_DICE_POLICY };
    const lim = Number.isFinite(p.dailyLimit) ? Math.max(1, Math.min(99, Math.floor(p.dailyLimit))) : DEFAULT_DICE_POLICY.dailyLimit;
    return { mode: p.mode, dailyLimit: lim };
  } catch {
    return { ...DEFAULT_DICE_POLICY };
  }
}
function writeDicePolicy(p: DicePolicy): void { safeSet(KEY_DICE_POLICY, JSON.stringify(p)); }
function readDicePolicyHistory(): DicePolicyChange[] {
  const raw = safeGet(KEY_DICE_POLICY_HISTORY);
  if (!raw) return [];
  try { const a = JSON.parse(raw); return Array.isArray(a) ? a as DicePolicyChange[] : []; } catch { return []; }
}
function appendDicePolicyHistory(entry: DicePolicyChange): void {
  const prev = readDicePolicyHistory();
  safeSet(KEY_DICE_POLICY_HISTORY, JSON.stringify([entry, ...prev].slice(0, 20)));
}

function kstYmd(): string {
  const d = new Date(Date.now() + 9 * 3600 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// ─── Firestore 공통 설정 → 로컬 캐시 동기화 (사용자 지시: 어드민 저장값이 실제 적용) ───

/**
 * Firestore SSoT 로부터 marble 공개 설정을 가져와 로컬 캐시(KEY_SETTINGS/KEY_DICE_POLICY)에 반영.
 * 테스트 환경에서는 no-op. 실패 시 getter 는 fail-open (stale cache 반환) — 실제 플레이는
 * rollDice 안에서 assertTournamentGameAccess 가 다시 BE 재검증하므로 fail-closed 보장됨.
 */
async function syncMarbleFromFirestore(): Promise<void> {
  if (process.env.NODE_ENV === 'test') return;
  try {
    const pub = await callGetPublicTournamentGameSettings('marble');
    const settings = readSettings();
    settings.enabled = pub.enabled;
    settings.eventStartAt = pub.openStartAt ? Date.parse(pub.openStartAt) : null;
    settings.eventEndAt = pub.openEndAt ? Date.parse(pub.openEndAt) : null;
    if (pub.gracePeriodMinutes != null && Number.isFinite(pub.gracePeriodMinutes)) {
      settings.gracePeriodMinutes = pub.gracePeriodMinutes;
    }
    if (Number.isFinite(pub.leaderboardEpoch) && pub.leaderboardEpoch > 0) {
      settings.leaderboardEpoch = pub.leaderboardEpoch;
    }
    writeJson(KEY_SETTINGS, settings);
    if (pub.dicePolicy && (pub.dicePolicy.mode === 'limited' || pub.dicePolicy.mode === 'unlimited')) {
      const n = Math.max(1, Math.min(99, Math.floor(Number(pub.dicePolicy.dailyLimit) || DEFAULT_DICE_POLICY.dailyLimit)));
      writeDicePolicy({ mode: pub.dicePolicy.mode, dailyLimit: n });
    }
  } catch {
    // V70-A — 설정 조회 실패 시 공개로 두지 않음 (fail-closed): 캐시의 enabled 를 false 로 내림.
    //   다음 조회가 성공하면 서버 값으로 복구된다. 기간·정책 캐시는 기존 값 유지.
    try {
      const settings = readSettings();
      settings.enabled = false;
      writeJson(KEY_SETTINGS, settings);
    } catch { /* storage 불가 — 아래 getter 는 캐시 기본값 사용 */ }
  }
}

/**
 * rollDice 안에서 호출 — access 응답으로 로컬 캐시 즉시 갱신 (별도 BE 호출 불필요).
 * fail-closed: assertTournamentGameAccess 자체가 실패 시 throw 하므로 이 함수는 성공 경로에만 호출.
 */
function applyAccessRespToLocalCache(access: AccessCheckResp): void {
  if (process.env.NODE_ENV === 'test') return;
  try {
    const settings = readSettings();
    settings.enabled = true;  // allowed=true
    settings.eventStartAt = access.openStartAt ? Date.parse(access.openStartAt) : null;
    settings.eventEndAt = access.openEndAt ? Date.parse(access.openEndAt) : null;
    if (access.gracePeriodMinutes != null && Number.isFinite(access.gracePeriodMinutes)) {
      settings.gracePeriodMinutes = access.gracePeriodMinutes;
    }
    if (access.leaderboardEpoch != null && Number.isFinite(access.leaderboardEpoch) && access.leaderboardEpoch > 0) {
      settings.leaderboardEpoch = access.leaderboardEpoch;
    }
    writeJson(KEY_SETTINGS, settings);
    if (access.policy && (access.policy.mode === 'limited' || access.policy.mode === 'unlimited')) {
      const n = Math.max(1, Math.min(99, Math.floor(Number(access.policy.dailyLimit) || DEFAULT_DICE_POLICY.dailyLimit)));
      writeDicePolicy({ mode: access.policy.mode, dailyLimit: n });
    }
  } catch {
    // cache 쓰기 실패는 플레이 자체를 막지 않음 (BE 재검증이 이미 통과함)
  }
}

// ─────────── Settings / Board ───────────

const DEFAULT_SETTINGS: MarbleSettings = {
  enabled: true,
  diceDailyLimit: MARBLE_GLOBAL_CONFIG.diceDailyLimit,
  eventStartAt: null,
  eventEndAt: null,
  gracePeriodMinutes: MARBLE_GLOBAL_CONFIG.gracePeriodMinutes,
  leaderboardCacheTtlSec: MARBLE_GLOBAL_CONFIG.leaderboardCacheTtlSec,
  leaderboardEpoch: 1,
  officialRankingStatus: 'IN_PROGRESS',
};

function readSettings(): MarbleSettings {
  return readJson<MarbleSettings>(KEY_SETTINGS, DEFAULT_SETTINGS);
}

function readBoard(epoch: number): MarbleBoard {
  const stored = readJson<MarbleBoard | null>(KEY_BOARD, null);
  if (stored && stored.epoch === epoch) return stored;
  const next: MarbleBoard = { epoch, cells: generateBoard(epoch) };
  writeJson(KEY_BOARD, next);
  return next;
}

// ─────────── MyState / Record ───────────

function emptyState(uid: string, displayName: string, orgUnitId: string | undefined, settings: MarbleSettings, now: number): MyState {
  const policy = readDicePolicy();
  return {
    uid, displayName, orgUnitId,
    position: 0, totalScore: 0,
    diceToday: policy.mode === 'limited' ? policy.dailyLimit : 999,
    diceUsedToday: 0, diceDateYmd: kstYmd(),
    inProgressEvent: null, playCount: 0, highScoreEvents: 0, lossSum: 0,
    epoch: settings.leaderboardEpoch, updatedAt: now,
    // V2
    items: { shields: 0, speedBoosts: 0 },
    isTrapped: false, trapFails: 0,
    lapsCompleted: 0, lapsReward: 0,
  };
}

function recomputeDiceToday(state: MyState, policy: DicePolicy): void {
  if (policy.mode === 'limited') {
    state.diceToday = Math.max(0, policy.dailyLimit - state.diceUsedToday);
  } else {
    state.diceToday = 999;
  }
}

/** V1 → V2 필드 기본값 보강 (기존 데이터 보존) */
function applyV2Defaults(state: Record<string, unknown>): MyState {
  if (!state.items) {
    const legacyShields = typeof state.shields === 'number' ? state.shields : 0;
    state.items = { shields: legacyShields, speedBoosts: 0 };
  }
  if (state.isTrapped == null) { state.isTrapped = false; state.trapFails = 0; }
  if (state.lapsCompleted == null) { state.lapsCompleted = 0; state.lapsReward = 0; }
  return state as unknown as MyState;
}

/**
 * V2 레코드 유효성 검증.
 * schemaVersion/state/필수 필드 검사.
 */
function isValidV2Record(parsed: unknown): parsed is MarbleStorageRecord {
  if (!parsed || typeof parsed !== 'object') return false;
  const p = parsed as Partial<MarbleStorageRecord>;
  if (p.schemaVersion !== 2) return false;
  if (!p.state || typeof p.state !== 'object') return false;
  const s = p.state as Partial<MyState>;
  // 필수 필드 shape 검증 (존재 여부만, 깊은 유효성은 처리 시점에)
  if (typeof s.uid !== 'string') return false;
  if (typeof s.position !== 'number' || !Number.isFinite(s.position)) return false;
  if (typeof s.totalScore !== 'number' || !Number.isFinite(s.totalScore)) return false;
  if (typeof s.epoch !== 'number') return false;
  // processedRequests 는 선택적이지만 object 이어야 함
  if (p.processedRequests != null && typeof p.processedRequests !== 'object') return false;
  return true;
}

/** 레코드 로드 (V2 우선, V1 fallback 마이그). 손상 시 V2CorruptError throw. */
function loadRecord(uid: string, displayName: string, orgUnitId: string | undefined): MarbleStorageRecord {
  const settings = readSettings();
  const now = Date.now();

  // 1차: V2 레코드
  const rawV2 = safeGet(recordKey(uid));
  if (rawV2) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawV2);
    } catch {
      // V2 JSON 파싱 실패 — 손상. 자동 복원 금지. 상태 변경 차단.
      // 원본 레코드는 삭제·덮어쓰지 않음 (사용자 지시 §1).
      throw new V2CorruptError(uid, 'V2 레코드 파싱 실패 — 저장된 데이터를 확인해 주세요.');
    }
    if (!isValidV2Record(parsed)) {
      // schema 검증 실패 — 손상. 동일 보호 정책.
      throw new V2CorruptError(uid, 'V2 레코드 스키마 검증 실패 — 저장된 데이터를 확인해 주세요.');
    }
    const record = parsed;
    // 세대 미스매치 → 리셋
    if (record.state.epoch !== settings.leaderboardEpoch) {
      const fresh = newRecord(uid, displayName, orgUnitId, settings, now);
      saveRecord(uid, fresh);
      return fresh;
    }
    // 자정 리셋
    const today = kstYmd();
    if (record.state.diceDateYmd !== today) {
      record.state.diceDateYmd = today;
      record.state.diceUsedToday = 0;
    }
    recomputeDiceToday(record.state, readDicePolicy());
    record.state.displayName = displayName || record.state.displayName;
    if (orgUnitId !== undefined) record.state.orgUnitId = orgUnitId;
    const pruned = pruneProcessedRequests(record);
    saveRecord(uid, pruned);
    return pruned;
  }

  // 2차: V1 레거시 마이그
  const legacyState = readJson<Record<string, unknown> | null>(V1_STATE_KEY_PREFIX + uid, null);
  if (legacyState) {
    const migrated = applyV2Defaults(legacyState);
    // 세대 미스매치 → 리셋
    if (migrated.epoch !== settings.leaderboardEpoch) {
      const fresh = newRecord(uid, displayName, orgUnitId, settings, now);
      saveRecord(uid, fresh);
      return fresh;
    }
    migrated.displayName = displayName || migrated.displayName;
    if (orgUnitId !== undefined) migrated.orgUnitId = orgUnitId;
    const today = kstYmd();
    if (migrated.diceDateYmd !== today) {
      migrated.diceDateYmd = today;
      migrated.diceUsedToday = 0;
    }
    recomputeDiceToday(migrated, readDicePolicy());
    // V1 응답 캐시 → processedRequests 이관 (라벨만 보존, response null → 재조회 유도)
    const processedRequests: MarbleStorageRecord['processedRequests'] = {};
    const migratedTs = now;
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (!key) continue;
        const prefix = V1_RESP_KEY_PREFIX + uid + '.';
        if (key.startsWith(prefix)) {
          const reqId = key.substring(prefix.length);
          processedRequests[reqId] = { response: null, ts: migratedTs };
        }
      }
    } catch { /* ignore */ }

    const record: MarbleStorageRecord = {
      schemaVersion: 2,
      state: migrated,
      processedRequests,
    };
    saveRecord(uid, record);
    trackLeaderboardUid(uid);
    // V1 키는 삭제하지 않음 (롤백 안전)
    return record;
  }

  // 3차: 신규
  const fresh = newRecord(uid, displayName, orgUnitId, settings, now);
  saveRecord(uid, fresh);
  trackLeaderboardUid(uid);
  return fresh;
}

function newRecord(uid: string, displayName: string, orgUnitId: string | undefined, settings: MarbleSettings, now: number): MarbleStorageRecord {
  return {
    schemaVersion: 2,
    state: emptyState(uid, displayName, orgUnitId, settings, now),
    processedRequests: {},
  };
}

function saveRecord(uid: string, record: MarbleStorageRecord): void {
  writeJson(recordKey(uid), record);
}

function pruneProcessedRequests(record: MarbleStorageRecord): MarbleStorageRecord {
  const now = Date.now();
  const entries = Object.entries(record.processedRequests || {});
  const kept = entries
    .filter(([, v]) => now - v.ts <= PROCESSED_REQUESTS_TTL_MS)
    .sort((a, b) => b[1].ts - a[1].ts)
    .slice(0, MAX_PROCESSED_REQUESTS);
  return {
    ...record,
    processedRequests: Object.fromEntries(kept),
  };
}

function trackLeaderboardUid(uid: string): void {
  const list = readJson<string[]>(KEY_LEADERBOARD_KEYS, []);
  if (!list.includes(uid)) {
    list.push(uid);
    writeJson(KEY_LEADERBOARD_KEYS, list);
  }
}

/** 단일 레코드 멱등 처리 wrapper */
function processWithIdempotency<Res>(
  uid: string,
  displayName: string,
  orgUnitId: string | undefined,
  reqId: string,
  handler: (record: MarbleStorageRecord) => { response: Res; nextRecord?: MarbleStorageRecord },
): Res {
  const record = loadRecord(uid, displayName, orgUnitId);
  const cached = record.processedRequests[reqId];
  if (cached && cached.response != null) return cached.response as Res;

  const { response, nextRecord } = handler(record);
  const finalRecord: MarbleStorageRecord = nextRecord ?? record;
  finalRecord.processedRequests = {
    ...finalRecord.processedRequests,
    [reqId]: { response, ts: Date.now() },
  };
  saveRecord(uid, finalRecord);
  return response;
}

// ─────────── 이벤트 처리 유틸 ───────────

function buildPendingEvent(kind: EventKind, now: number, board: MarbleBoard, selfPos: number): InProgressEventState {
  const eventId = `${kind}-${now}-${Math.random().toString(36).slice(2, 8)}`;
  switch (kind) {
    case 'E1':
      return { eventId, kind, createdAt: now, options: E1_CONFIG.options.map(o => ({ id: o.id, label: o.label, rewards: [...o.rewards] })) };
    case 'E2':
      return { eventId, kind, createdAt: now, options: [
        { id: 'rock', label: '바위', rewards: [...E2_CONFIG.outcomes] },
        { id: 'paper', label: '보', rewards: [...E2_CONFIG.outcomes] },
        { id: 'scissors', label: '가위', rewards: [...E2_CONFIG.outcomes] },
      ]};
    case 'E3':
      return { eventId, kind, createdAt: now, options: E3_CONFIG.options.map(o => ({ id: o.id, label: o.label, rewards: [...o.rewards] })) };
    case 'E4':
      return { eventId, kind, createdAt: now, options: [
        { id: 'heads', label: '앞', rewards: [...E4_CONFIG.outcomes] },
        { id: 'tails', label: '뒤', rewards: [...E4_CONFIG.outcomes] },
      ]};
    case 'E5':
      return { eventId, kind, createdAt: now, options: E5_CONFIG.options.map(o => ({ id: o.id, label: o.label, rewards: [...o.rewards] })) };
    case 'E6':
      return { eventId, kind, createdAt: now, options: [0,1,2,3,4].map(i => ({ id: i, label: `카드 ${i+1}`, rewards: E6_CONFIG.deck.map(v => ({ value: v, prob: 1/5 })) })) };
    case 'E7':
      return { eventId, kind, createdAt: now, options: [
        { id: 'odd', label: '홀', rewards: [...E7_CONFIG.outcomes] },
        { id: 'even', label: '짝', rewards: [...E7_CONFIG.outcomes] },
      ]};
    case 'E9': {
      // V2: 23칸 자유 선택 — options 은 현 위치 제외 전체
      const opts = board.cells
        .filter(c => c.index !== selfPos)
        .map(c => ({ id: c.index, label: `${c.index + 1}번 (${cellKindShortLabel(c.index, c.kind)})` }));
      return { eventId, kind, createdAt: now, options: opts };
    }
    default:
      return { eventId, kind, createdAt: now };
  }
}

function cellKindShortLabel(index: number, kind: EventKind): string {
  if (kind === 'safe') {
    if (index === V2_SAFE_ROLES.START) return 'START';
    if (index === V2_SAFE_ROLES.TRAP) return '무인도';
    if (index === V2_SAFE_ROLES.VAULT) return '금고';
    if (index === V2_SAFE_ROLES.SHOP) return '상점';
    return '안전';
  }
  return kind;
}

function applyShieldAuto(state: MyState, delta: number): { finalDelta: number; shieldConsumed: boolean; grossLoss: number } {
  if (delta < 0 && state.items.shields > 0) {
    state.items.shields -= 1;
    return { finalDelta: 0, shieldConsumed: true, grossLoss: Math.abs(delta) };
  }
  return { finalDelta: delta, shieldConsumed: false, grossLoss: delta < 0 ? Math.abs(delta) : 0 };
}

function applyScore(state: MyState, delta: number, grossLoss: number): number {
  // ISSUE-07: 음수 0 clamp 제거. 사용자 지시 — 방패 미보유 상태 -100 획득 시 총점 -100 반영.
  //           방패 발동 시 (delta=0, grossLoss>0) 은 applyShieldAuto 가 delta=0 로 넘기므로 영향 없음.
  //           ISSUE-05 (방패 연출) 과 분리된 변경 — 방패 소비 로직은 applyShieldAuto 가 전담.
  const prev = state.totalScore;
  state.totalScore = state.totalScore + delta;
  const applied = state.totalScore - prev;
  if (grossLoss > 0) state.lossSum += grossLoss;
  if (Math.abs(delta) >= 200 && applied > 0) state.highScoreEvents += 1;
  return applied;
}

function applyLapsReward(state: MyState, laps: number): void {
  if (laps <= 0) return;
  state.lapsCompleted += laps;
  const reward = LAP_REWARD * laps;
  state.lapsReward += reward;
  applyScore(state, reward, 0);
}

/** safe 모서리 index 로 역할 분기 */
function resolveSafeImmediate(index: number, state: MyState, rand: () => number): { delta: number; label: string; shieldConsumed?: boolean; needsSelection?: 'vault' | 'shop' } {
  if (index === V2_SAFE_ROLES.START) {
    return { delta: 0, label: 'START (안전)' };
  }
  if (index === V2_SAFE_ROLES.TRAP) {
    state.isTrapped = true;
    state.trapFails = 0;
    return { delta: 0, label: '🏝 무인도 진입' };
  }
  if (index === V2_SAFE_ROLES.VAULT) {
    // 금고는 선택형 pending 생성 (vault)
    return { delta: 0, label: '💰 보너스 금고', needsSelection: 'vault' };
  }
  if (index === V2_SAFE_ROLES.SHOP) {
    // 상점은 선택형 pending 생성 (shop)
    return { delta: 0, label: '🏪 아이템 상점', needsSelection: 'shop' };
  }
  return { delta: 0, label: '안전' };
}

function resolveImmediateE8E10(kind: EventKind, state: MyState, rand: () => number): { delta: number; shieldConsumed: boolean; label?: string; grossLoss?: number } {
  if (kind === 'E8') {
    state.items.shields = Math.min(SHOP_CONFIG.shieldMax, state.items.shields + 1);
    return { delta: 0, shieldConsumed: false, label: '방패 획득' };
  }
  if (kind === 'E10') {
    const v = pickWeighted(rand, E10_CONFIG.dist);
    const match = E10_CONFIG.dist.find(d => d.value === v);
    const { finalDelta, shieldConsumed, grossLoss } = applyShieldAuto(state, v);
    applyScore(state, finalDelta, grossLoss);
    // ISSUE-05: 방패 발동 시 원래 손실 금액 포함 — 결과창 3단 연출용
    return { delta: finalDelta, shieldConsumed, label: match?.label, grossLoss: shieldConsumed ? grossLoss : undefined };
  }
  return { delta: 0, shieldConsumed: false, label: '안전' };
}

// ─────────── mock API 구현 ───────────

export const mockServerApi: MarbleServerApi = {
  async getSettings() {
    await syncMarbleFromFirestore();
    return readSettings();
  },

  async getBoard() {
    await syncMarbleFromFirestore();
    const s = readSettings();
    return readBoard(s.leaderboardEpoch);
  },

  async getMyState(uid, displayName, orgUnitId) {
    const record = loadRecord(uid, displayName, orgUnitId);
    return record.state;
  },

  async rollDice(uid, req) {
    // V70 Phase 3-A §1: 매 roll 전 BE 재검증 (Firestore 공통 설정 기반). fail-closed.
    // 응답으로 로컬 캐시 즉시 갱신 → Firestore SSoT 가 실제 플레이에 반영됨.
    const access = await assertTournamentGameAccess('marble');
    applyAccessRespToLocalCache(access);
    const settings = readSettings();
    if (!settings.enabled) throw new Error('현재 엔글 마블은 비공개 상태입니다.');
    const now = Date.now();
    if (settings.eventStartAt && now < settings.eventStartAt) throw new Error('대회가 아직 시작되지 않았습니다.');
    if (settings.eventEndAt && now >= settings.eventEndAt) throw new Error('대회가 종료되었습니다.');

    return processWithIdempotency<RollDiceResponse>(
      uid, '', undefined, req.clientRequestId,
      (record) => {
        const state = record.state;
        if (state.inProgressEvent) throw new Error('진행 중인 이벤트가 있습니다. 먼저 선택을 완료하세요.');

        const policy = readDicePolicy();
        if (policy.mode === 'limited') {
          const remaining = Math.max(0, policy.dailyLimit - state.diceUsedToday);
          if (remaining <= 0) throw new Error('오늘 사용 가능한 주사위를 모두 사용했습니다. 내일 00:00 KST 에 다시 도전 가능합니다.');
        }

        // 가속 사용 검증 (사용자 지시 §6.3)
        const useSpeed = req.useSpeedBoost === true;
        if (useSpeed) {
          if (state.isTrapped) throw new Error('무인도 탈출 중에는 가속을 사용할 수 없습니다.');
          if (state.items.speedBoosts <= 0) throw new Error('가속 아이템이 없습니다.');
        }

        const seed = `${uid}:${req.clientRequestId}:${settings.leaderboardEpoch}:${now}`;
        const rand = createPrng(hashSeed(seed));
        const board = readBoard(settings.leaderboardEpoch);

        const diceFace = Math.floor(rand() * MARBLE_GLOBAL_CONFIG.diceSides) + 1;
        state.diceUsedToday += 1;
        recomputeDiceToday(state, policy);
        state.playCount += 1;

        // ── 무인도 분기 ──
        if (state.isTrapped) {
          const isGuaranteed = state.trapFails >= TRAP_CONFIG.guaranteeAfterFails;
          const escaped = isGuaranteed || diceFace >= TRAP_CONFIG.successThreshold;
          if (!escaped) {
            state.trapFails += 1;
            state.updatedAt = now;
            const resp: RollDiceResponse = {
              rollId: `roll-${now}-${Math.random().toString(36).slice(2, 8)}`,
              dice: diceFace, from: state.position, to: state.position,
              cellKind: 'safe',
              immediate: {
                delta: 0, scoreAfter: state.totalScore,
                label: `🏝 탈출 실패 (주사위 ${diceFace}, ${state.trapFails}차 실패)`,
              },
              isMock: true,
            };
            return { response: resp };
          }
          // 성공 — 탈출 안내는 프론트 EscapeCheerOverlay 가 전담
          state.isTrapped = false;
          state.trapFails = 0;
          // 가속 미적용 — 사용자 지시 §4.1 ("무인도 중 가속 사용 불가")
          const moveDist = diceFace;
          return doMoveAfterEscape(state, moveDist, diceFace, board, rand, settings, now);
        }

        // ── 일반 roll ──
        if (useSpeed) {
          state.items.speedBoosts -= 1;
        }
        const moveDistance = useSpeed ? Math.min(diceFace + SPEED_CONFIG.bonus, SPEED_CONFIG.maxMoveDistance) : diceFace;
        return doMoveAndEvent(state, moveDistance, diceFace, useSpeed, board, rand, settings, now);
      },
    );
  },

  async submitEventChoice(uid, req) {
    const settings = readSettings();
    const now = Date.now();
    if (settings.eventEndAt && now >= settings.eventEndAt + settings.gracePeriodMinutes * 60_000) {
      // grace 초과 → 폐기
      const r = loadRecord(uid, '', undefined);
      r.state.inProgressEvent = null;
      saveRecord(uid, r);
      throw new Error('대회 종료 후 미완료 이벤트의 유효 시간이 초과되었습니다. 폐기되었습니다.');
    }

    return processWithIdempotency<SubmitEventChoiceResponse>(
      uid, '', undefined, req.clientRequestId,
      (record) => {
        const state = record.state;
        const pending = state.inProgressEvent;
        if (!pending || pending.eventId !== req.eventId) throw new Error('진행 중인 이벤트가 없거나 ID 가 일치하지 않습니다.');

        const seed = `${uid}:choice:${req.clientRequestId}:${settings.leaderboardEpoch}:${now}`;
        const rand = createPrng(hashSeed(seed));
        const choice = req.choice;
        const board = readBoard(settings.leaderboardEpoch);

        let delta = 0;
        let outcomeLabel: string | undefined;
        let shieldConsumed = false;
        let grossBeforeShield = 0;
        // §QA6 §1·§2: E7 추첨 숫자 (1~10). E7 분기에만 설정 — 다른 이벤트는 undefined 유지.
        let drawnNumber: number | undefined;

        if (pending.kind === 'E9') {
          // 워프 — 23칸 자유 선택
          const targetIdx = Number(choice);
          if (!Number.isFinite(targetIdx) || targetIdx < 0 || targetIdx >= 24) throw new Error('워프 대상이 유효하지 않습니다.');
          if (targetIdx === state.position) throw new Error('워프 대상이 현재 위치입니다.');
          const target = board.cells[targetIdx];
          if (target.kind === 'E9') throw new Error('워프 → 워프는 금지됩니다.');

          state.position = target.index;
          state.inProgressEvent = null;

          // 워프 자체는 완주 보상 미지급 (사용자 지시 §B1)
          // §QA4 §8: arrivalKind 명시 — target.kind 기반 구조화 분기 (문자열 파싱 X)
          if (target.kind === 'safe') {
            const safeImm = resolveSafeImmediate(target.index, state, rand);
            if (safeImm.needsSelection === 'vault') {
              const vp = buildVaultPending(now);
              state.inProgressEvent = vp;
              state.updatedAt = now;
              return {
                response: { eventId: pending.eventId, delta: 0, scoreAfter: state.totalScore, outcomeLabel: `워프 도착: ${safeImm.label}`, arrivalKind: 'pending', isMock: true },
              };
            } else if (safeImm.needsSelection === 'shop') {
              const sp = buildShopPending(now);
              state.inProgressEvent = sp;
              state.updatedAt = now;
              return {
                response: { eventId: pending.eventId, delta: 0, scoreAfter: state.totalScore, outcomeLabel: `워프 도착: ${safeImm.label}`, arrivalKind: 'pending', isMock: true },
              };
            }
            // safe (START / TRAP / 기타) — index 로 분기
            const arrivalKind: 'trap' | 'start' | 'none' =
              target.index === V2_SAFE_ROLES.TRAP ? 'trap'
              : target.index === V2_SAFE_ROLES.START ? 'start'
              : 'none';
            state.updatedAt = now;
            return {
              response: { eventId: pending.eventId, delta: 0, scoreAfter: state.totalScore, outcomeLabel: `워프 도착: ${safeImm.label}`, arrivalKind, isMock: true },
            };
          } else if (target.kind === 'E8') {
            const im = resolveImmediateE8E10('E8', state, rand);  // state.items.shields +1
            state.updatedAt = now;
            return {
              response: {
                eventId: pending.eventId, delta: im.delta, scoreAfter: state.totalScore,
                shieldConsumed: im.shieldConsumed,
                outcomeLabel: `워프 도착: ${im.label || 'E8'}`,
                arrivalKind: 'shield',
                shieldsOwnedAfter: state.items.shields,  // 보유량 정합 보증
                isMock: true,
              },
            };
          } else if (target.kind === 'E10') {
            const im = resolveImmediateE8E10('E10', state, rand);
            // §QA4 §3: E10 은 delta=0 ("꽝") 이어도 arrivalKind='score' 로 분류
            // ISSUE-05: 방패 발동 시 grossLoss 포함
            state.updatedAt = now;
            return {
              response: {
                eventId: pending.eventId, delta: im.delta, scoreAfter: state.totalScore,
                shieldConsumed: im.shieldConsumed,
                grossLoss: im.grossLoss,
                outcomeLabel: `워프 도착: ${im.label || 'E10'}`,
                arrivalKind: 'score',
                isMock: true,
              },
            };
          } else {
            // E1/E2/E3/E4/E5/E6/E7 — 도착 칸 이벤트 재발동 (체인 깊이 1)
            const nextPending = buildPendingEvent(target.kind, now, board, target.index);
            state.inProgressEvent = nextPending;
            state.updatedAt = now;
            return {
              response: { eventId: pending.eventId, delta: 0, scoreAfter: state.totalScore, outcomeLabel: `워프 도착: ${target.kind}`, arrivalKind: 'pending', isMock: true },
            };
          }
        }

        if (pending.kind === 'vault' as EventKind) {
          // 보너스 금고
          const opt = VAULT_CONFIG.options.find(o => o.id === String(choice));
          if (!opt) throw new Error('금고 선택이 유효하지 않습니다.');
          const v = pickWeighted(rand, opt.rewards);
          const found = opt.rewards.find(r => r.value === v);
          outcomeLabel = found?.label || opt.label;
          grossBeforeShield = v;
        } else if (pending.kind === 'shop' as EventKind) {
          // 상점 — choice 는 'shield' | 'speedBoost' | 'pass'
          if (choice === 'pass') {
            state.inProgressEvent = null;
            state.updatedAt = now;
            return {
              response: { eventId: pending.eventId, delta: 0, scoreAfter: state.totalScore, outcomeLabel: '상점 통과', isMock: true },
            };
          }
          if (choice === 'shield') {
            if (state.totalScore < SHOP_CONFIG.shieldPrice) throw new Error('점수가 부족합니다.');
            if (state.items.shields >= SHOP_CONFIG.shieldMax) throw new Error('방패 최대 보유 (3개) 입니다.');
            state.totalScore -= SHOP_CONFIG.shieldPrice;
            state.items.shields += 1;
            state.inProgressEvent = null;
            state.updatedAt = now;
            return {
              response: { eventId: pending.eventId, delta: -SHOP_CONFIG.shieldPrice, scoreAfter: state.totalScore, outcomeLabel: `🛡 방패 구매 (${state.items.shields}/${SHOP_CONFIG.shieldMax})`, isMock: true },
            };
          }
          if (choice === 'speedBoost') {
            if (state.totalScore < SHOP_CONFIG.speedPrice) throw new Error('점수가 부족합니다.');
            if (state.items.speedBoosts >= SHOP_CONFIG.speedMax) throw new Error('가속 최대 보유 (1개) 입니다.');
            state.totalScore -= SHOP_CONFIG.speedPrice;
            state.items.speedBoosts += 1;
            state.inProgressEvent = null;
            state.updatedAt = now;
            return {
              response: { eventId: pending.eventId, delta: -SHOP_CONFIG.speedPrice, scoreAfter: state.totalScore, outcomeLabel: `🚀 가속 구매 (${state.items.speedBoosts}/${SHOP_CONFIG.speedMax})`, isMock: true },
            };
          }
          throw new Error('상점 선택이 유효하지 않습니다.');
        } else {
          // 일반 선택형 이벤트
          switch (pending.kind) {
            case 'E1': {
              const opt = E1_CONFIG.options.find(o => o.id === choice);
              if (!opt) throw new Error('선택지가 유효하지 않습니다.');
              const v = pickWeighted(rand, opt.rewards);
              const found = opt.rewards.find(r => r.value === v);
              outcomeLabel = found?.label || opt.label;
              grossBeforeShield = v;
              break;
            }
            case 'E2': {
              const v = pickWeighted(rand, E2_CONFIG.outcomes);
              const found = E2_CONFIG.outcomes.find(r => r.value === v);
              outcomeLabel = found?.label;
              grossBeforeShield = v;
              break;
            }
            case 'E3': {
              const opt = E3_CONFIG.options.find(o => o.id === choice);
              if (!opt) throw new Error('선택지가 유효하지 않습니다.');
              const v = pickWeighted(rand, opt.rewards);
              outcomeLabel = opt.label;
              grossBeforeShield = v;
              break;
            }
            case 'E4': {
              const v = pickWeighted(rand, E4_CONFIG.outcomes);
              outcomeLabel = v > 0 ? '적중' : '미적중';
              grossBeforeShield = v;
              break;
            }
            case 'E5': {
              const opt = E5_CONFIG.options.find(o => String(o.id) === String(choice));
              if (!opt) throw new Error('선택지가 유효하지 않습니다.');
              const v = pickWeighted(rand, opt.rewards);
              outcomeLabel = opt.label;
              grossBeforeShield = v;
              break;
            }
            case 'E6': {
              const i = Math.floor(rand() * 5);
              const v = E6_CONFIG.deck[i];
              outcomeLabel = `카드 ${i + 1}`;
              grossBeforeShield = v;
              break;
            }
            case 'E7': {
              // §QA6 §1: 1~10 균등 추첨 → 홀짝 판정 → 사용자 선택과 비교. 보상은 E7_CONFIG 단일 참조.
              if (choice !== 'odd' && choice !== 'even') throw new Error('E7 선택이 유효하지 않습니다.');
              const n = 1 + Math.floor(rand() * 10);  // 1~10 균등 (서버 PRNG 결정론)
              if (n < 1 || n > 10) throw new Error('E7 추첨 숫자 범위 오류');
              const drawnParity: 'odd' | 'even' = n % 2 === 1 ? 'odd' : 'even';
              const hit = drawnParity === choice;
              // E7_CONFIG 단일 참조 — 하드코딩 fallback 금지 (사용자 지시 §1)
              const hitEntry = E7_CONFIG.outcomes.find(o => o.label === '적중');
              const missEntry = E7_CONFIG.outcomes.find(o => o.label === '미적중');
              if (!hitEntry || !missEntry) throw new Error('E7_CONFIG 보상 수치가 손상되었습니다.');
              const v = hit ? hitEntry.value : missEntry.value;
              outcomeLabel = hit ? '적중' : '미적중';
              grossBeforeShield = v;
              drawnNumber = n;
              break;
            }
            default:
              throw new Error('지원되지 않는 이벤트.');
          }
        }

        const sh = applyShieldAuto(state, grossBeforeShield);
        delta = sh.finalDelta;
        shieldConsumed = sh.shieldConsumed;
        applyScore(state, sh.finalDelta, sh.grossLoss);
        state.inProgressEvent = null;
        state.updatedAt = now;

        // ISSUE-05: 방패 발동 시 원래 손실 금액 (grossLoss) 응답 포함 — 결과창 3단 연출 ("-100 → 방패 → 0")
        return {
          response: {
            eventId: pending.eventId,
            delta,
            scoreAfter: state.totalScore,
            shieldConsumed,
            grossLoss: shieldConsumed ? sh.grossLoss : undefined,
            outcomeLabel,
            drawnNumber,
            isMock: true,
          },
        };
      },
    );
  },

  async purchaseItem(uid, req) {
    return processWithIdempotency<PurchaseItemResponse>(
      uid, '', undefined, req.clientRequestId,
      (record) => {
        const state = record.state;
        const now = Date.now();
        let price = 0;
        if (req.item === 'shield') {
          if (state.items.shields >= SHOP_CONFIG.shieldMax) throw new Error('방패 최대 보유.');
          price = SHOP_CONFIG.shieldPrice;
          if (state.totalScore < price) throw new Error('점수가 부족합니다.');
          state.totalScore -= price;
          state.items.shields += 1;
        } else if (req.item === 'speedBoost') {
          if (state.items.speedBoosts >= SHOP_CONFIG.speedMax) throw new Error('가속 최대 보유.');
          price = SHOP_CONFIG.speedPrice;
          if (state.totalScore < price) throw new Error('점수가 부족합니다.');
          state.totalScore -= price;
          state.items.speedBoosts += 1;
        } else {
          throw new Error('지원되지 않는 아이템.');
        }
        state.updatedAt = now;
        return {
          response: {
            success: true, item: req.item, priceDeducted: price,
            scoreAfter: state.totalScore,
            itemsAfter: { shields: state.items.shields, speedBoosts: state.items.speedBoosts },
            isMock: true,
          },
        };
      },
    );
  },

  async getLeaderboard(selfUid) {
    await syncMarbleFromFirestore();
    // V70-A — 공식 리더보드는 Firestore 에 저장된 유효 기록만 표시한다 (서버 모드).
    //   mock(브라우저 저장) 모드의 localStorage 기록은 전사 공식 순위처럼 노출하지 않으므로 항상 빈 목록.
    //   (가상 데이터로 채우지 않음)
    void selfUid;
    return { rows: [], self: null, cachedAt: Date.now() };
  },

  async qaResetMyState(uid, _clientRequestId) {
    safeRemove(recordKey(uid));
    safeRemove(V1_STATE_KEY_PREFIX + uid);
    return { ok: true };
  },

  async qaRefillDice(uid, dice, _clientRequestId) {
    const r = loadRecord(uid, '', undefined);
    const policy = readDicePolicy();
    const wanted = Math.max(0, Math.min(99, dice));
    if (policy.mode === 'limited') {
      r.state.diceUsedToday = Math.max(0, policy.dailyLimit - wanted);
    } else {
      r.state.diceUsedToday = 0;
    }
    r.state.diceDateYmd = kstYmd();
    recomputeDiceToday(r.state, policy);
    saveRecord(uid, r);
    return { ok: true };
  },

  async qaResetAll() {
    const uids = readJson<string[]>(KEY_LEADERBOARD_KEYS, []);
    for (const u of uids) {
      safeRemove(recordKey(u));
      safeRemove(V1_STATE_KEY_PREFIX + u);
    }
    safeRemove(KEY_LEADERBOARD_KEYS);
    safeRemove(KEY_BOARD);
    safeRemove(KEY_SETTINGS);
    return { ok: true };
  },

  async updateOpenPeriod(req) {
    const start = req.openStartAt;
    const end = req.openEndAt;
    if (start != null && end != null && start >= end) {
      throw new Error('종료 일시는 시작 일시보다 뒤여야 합니다.');
    }
    // V70 Phase 3-A §3: Firestore 전역 저장 (SSoT). 로컬 cache 는 fallback 으로 유지.
    await callUpdateTournamentOpenPeriod(
      'marble',
      start != null ? new Date(start).toISOString() : null,
      end != null ? new Date(end).toISOString() : null,
    );
    const settings = readSettings();
    settings.eventStartAt = start;
    settings.eventEndAt = end;
    writeJson(KEY_SETTINGS, settings);
    return { success: true, openStartAt: start, openEndAt: end, isMock: true };
  },

  async getDicePolicy() {
    await syncMarbleFromFirestore();
    return readDicePolicy();
  },

  async getDailyDiceStatus(uid) {
    await syncMarbleFromFirestore();
    const r = loadRecord(uid, '', undefined);
    const policy = readDicePolicy();
    const used = r.state.diceUsedToday;
    return {
      mode: policy.mode, used,
      limit: policy.mode === 'limited' ? policy.dailyLimit : null,
      remaining: policy.mode === 'limited' ? Math.max(0, policy.dailyLimit - used) : null,
      diceDateYmd: r.state.diceDateYmd,
    };
  },

  async updateDicePolicy(req) {
    if (!req.clientRequestId) throw new Error('clientRequestId 누락.');
    const { policy } = req;
    if (policy.mode !== 'unlimited' && policy.mode !== 'limited') throw new Error('mode 는 unlimited 또는 limited 이어야 합니다.');
    if (policy.mode === 'limited') {
      if (!Number.isFinite(policy.dailyLimit)) throw new Error('dailyLimit 은 정수여야 합니다.');
      const n = Math.floor(policy.dailyLimit);
      if (n < 1 || n > 99) throw new Error('dailyLimit 은 1~99 사이여야 합니다.');
    }
    const prev = readDicePolicy();
    const next: DicePolicy = {
      mode: policy.mode,
      dailyLimit: policy.mode === 'limited' ? Math.floor(policy.dailyLimit) : prev.dailyLimit,
    };
    // V70 Phase 3-A §3: Firestore 전역 저장 (SSoT). 로컬 cache 는 fallback.
    await callUpdateTournamentDicePolicy({ mode: next.mode, dailyLimit: next.dailyLimit });
    writeDicePolicy(next);
    const change: DicePolicyChange = {
      id: `dicepol-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      changedAt: Date.now(),
      changedByName: '현재 관리자 (mock)',
      previous: prev, next,
    };
    appendDicePolicyHistory(change);
    return { success: true, previous: prev, next, changedAt: change.changedAt, isMock: true };
  },

  async getDicePolicyHistory(opts) {
    const limit = Math.max(1, Math.min(20, opts?.limit ?? 10));
    return readDicePolicyHistory().slice(0, limit);
  },
};

// ─────────── 보드 이동 로직 (helpers for rollDice) ───────────

function doMoveAndEvent(
  state: MyState, moveDistance: number, diceFace: number, usedSpeed: boolean,
  board: MarbleBoard, rand: () => number, settings: MarbleSettings, now: number,
): { response: RollDiceResponse } {
  const from = state.position;
  const absoluteTo = from + moveDistance;
  const laps = Math.floor(absoluteTo / 24);
  const to = absoluteTo % 24;
  state.position = to;
  applyLapsReward(state, laps);
  return finalizeArrival(state, to, from, diceFace, moveDistance, usedSpeed, board, rand, settings, now, laps);
}

function doMoveAfterEscape(
  state: MyState, moveDistance: number, diceFace: number,
  board: MarbleBoard, rand: () => number, settings: MarbleSettings, now: number,
): { response: RollDiceResponse } {
  const from = state.position;
  const absoluteTo = from + moveDistance;
  const laps = Math.floor(absoluteTo / 24);
  const to = absoluteTo % 24;
  state.position = to;
  applyLapsReward(state, laps);
  // §3: 라벨 접붙임 제거. 탈출 안내는 프론트 EscapeCheerOverlay 가 전담.
  return finalizeArrival(state, to, from, diceFace, moveDistance, false, board, rand, settings, now, laps);
}

function finalizeArrival(
  state: MyState, to: number, from: number, diceFace: number, moveDistance: number, usedSpeed: boolean,
  board: MarbleBoard, rand: () => number, settings: MarbleSettings, now: number, laps: number,
): { response: RollDiceResponse } {
  const cell = board.cells[to];
  const rollId = `roll-${now}-${Math.random().toString(36).slice(2, 8)}`;
  // §1: 바퀴 보상은 totalScore 에 이미 반영됨 (applyLapsReward). 응답 delta/lapsReward 에만 추가 보정.
  const lapsReward = laps > 0 ? LAP_REWARD * laps : 0;
  let response: RollDiceResponse;

  if (cell.kind === 'safe') {
    const safe = resolveSafeImmediate(to, state, rand);
    if (safe.needsSelection === 'vault') {
      const pending = buildVaultPending(now);
      state.inProgressEvent = pending;
      response = { rollId, dice: diceFace, from, to, cellKind: 'safe', pending, isMock: true };
    } else if (safe.needsSelection === 'shop') {
      const pending = buildShopPending(now);
      state.inProgressEvent = pending;
      response = { rollId, dice: diceFace, from, to, cellKind: 'safe', pending, isMock: true };
    } else {
      // §QA3 §1: safe label 자체는 점수 보상 없음 (바퀴는 lapsReward 로 완전 분리)
      response = {
        rollId, dice: diceFace, from, to, cellKind: 'safe',
        immediate: { delta: 0, scoreAfter: state.totalScore, label: safe.label },
        isMock: true,
      };
    }
  } else if (cell.kind === 'E8' || cell.kind === 'E10') {
    const im = resolveImmediateE8E10(cell.kind, state, rand);
    // §QA3 §1: delta 는 이벤트 보상만, 바퀴 보상은 lapsReward 로 완전 분리
    // ISSUE-05: 방패 발동 시 grossLoss 포함
    response = {
      rollId, dice: diceFace, from, to, cellKind: cell.kind,
      immediate: { delta: im.delta, scoreAfter: state.totalScore, shieldConsumed: im.shieldConsumed, label: im.label || '', grossLoss: im.grossLoss },
      isMock: true,
    };
  } else if (cell.kind === 'E9') {
    const pending = buildPendingEvent('E9', now, board, to);
    state.inProgressEvent = pending;
    response = { rollId, dice: diceFace, from, to, cellKind: 'E9', pending, isMock: true };
  } else {
    const pending = buildPendingEvent(cell.kind, now, board, to);
    state.inProgressEvent = pending;
    response = { rollId, dice: diceFace, from, to, cellKind: cell.kind, pending, isMock: true };
  }

  if (lapsReward > 0) {
    response.lapsReward = lapsReward;
  }

  state.updatedAt = now;
  return { response };
}

// 금고/상점 pending shape (일반 선택형 pending 과 호환)
function buildVaultPending(now: number): InProgressEventState {
  return {
    eventId: `VAULT-${now}-${Math.random().toString(36).slice(2, 8)}`,
    kind: 'vault' as EventKind,
    createdAt: now,
    options: VAULT_CONFIG.options.map(o => ({ id: o.id, label: o.label, rewards: [...o.rewards] })),
  };
}
function buildShopPending(now: number): InProgressEventState {
  return {
    eventId: `SHOP-${now}-${Math.random().toString(36).slice(2, 8)}`,
    kind: 'shop' as EventKind,
    createdAt: now,
    options: [
      { id: 'shield', label: `🛡 방패 (${SHOP_CONFIG.shieldPrice}점)` },
      { id: 'speedBoost', label: `🚀 가속 (${SHOP_CONFIG.speedPrice}점)` },
      { id: 'pass', label: '그냥 지나가기' },
    ],
  };
}

// V70 Phase 3-C: Feature flag 분기 — QA + env flag 둘 다 ON 이면 서버 serverApi 사용.
// OFF 상태에서는 기존 mockServerApi 유지 (운영 보호). import 는 파일 상단으로 이동 (ESLint import/first).
export const marbleApi: MarbleServerApi = isPhase3cServerEnabled() ? serverMarbleApi : mockServerApi;
